import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@utils/Query";
import { devicesApis } from "@queries/Devices";
import useOrganization from "@hooks/useOrganization";
import { useDevicesWithLinkedLicenses } from "./useDevicesWithLinkedLicenses";

export const useThumbnail = ({
  deviceId,
  channel,
  disabled
}: {
  deviceId: string;
  channel: number;
  disabled?: boolean;
}) => {
  const { orgId } = useOrganization();

  // fetch available devices
  const { allDevicesWithLinkedLicenses: devices, isLoading: isDevicesLoading } =
    useDevicesWithLinkedLicenses(orgId);

  // check if device exists by deviceId and check if connection status is online or not
  const selectedDevice = useMemo(() => {
    return devices.find(
      (device) =>
        device.gatewayId === deviceId && device.connectionStatus === "online"
    );
  }, [devices, deviceId]);

  // conditionally enable query
  const isSnapshotQueryEnabled = !!orgId && !!selectedDevice && !disabled;

  const {
    data: snapshot,
    isLoading: isSnapshotLoading,
    isError: isSnapshotError
  } = useQuery(
    [queryKeys(`SUNAPI-${deviceId}-snapshot`).getAll(), orgId],
    ({ signal }) =>
      devicesApis.getSnapshot(orgId!, deviceId, { channel }, signal),
    {
      enabled: isSnapshotQueryEnabled,
      retry: false
    }
  );

  // Combined loading and error state
  const isLoading =
    isDevicesLoading || (isSnapshotQueryEnabled && isSnapshotLoading);
  const isError = isSnapshotQueryEnabled && isSnapshotError;

  return {
    data: snapshot,
    // If device not found or not online the return false else loading state
    isLoading,
    isError,
    isOnline: !!selectedDevice
  };
};
